import { z } from 'zod';
import { BillDataSchema, type BillData } from '@shared/schema/billData';
import type { FieldValidationError } from '@shared/schema/validationError';

/**
 * Client-side call into OUR backend's `/api/bills/analyze` — never calls
 * Gemini directly (the API key never ships to the browser; see
 * functions/lib/gemini/geminiBillExtractor.ts and docs/privacy.md).
 */
export interface AnalyzeBillResult {
  requestId: string;
  billData: BillData;
  warnings: FieldValidationError[];
}

export class AnalyzeBillError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AnalyzeBillError';
  }
}

const FieldValidationErrorSchema = z.object({ field: z.string(), code: z.string(), message: z.string() });

const AnalyzeResponseSchema = z.union([
  z.object({
    ok: z.literal(true),
    requestId: z.string(),
    billData: BillDataSchema,
    warnings: z.array(FieldValidationErrorSchema),
  }),
  z.object({
    ok: z.literal(false),
    error: z.object({ code: z.string(), message: z.string() }),
  }),
]);

const GENERIC_FAILURE_MESSAGE = "We couldn't analyze this bill. Please try again.";

export async function analyzeBill(image: Blob, filename = 'bill.jpg'): Promise<AnalyzeBillResult> {
  const formData = new FormData();
  formData.append('image', image, filename);

  let response: Response;
  try {
    response = await fetch('/api/bills/analyze', { method: 'POST', body: formData });
  } catch {
    throw new AnalyzeBillError('network_error', "We couldn't reach the server. Check your connection and try again.");
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    throw new AnalyzeBillError('invalid_response', GENERIC_FAILURE_MESSAGE);
  }

  const parsed = AnalyzeResponseSchema.safeParse(json);
  if (!parsed.success) {
    throw new AnalyzeBillError('invalid_response', GENERIC_FAILURE_MESSAGE);
  }
  if (!parsed.data.ok) {
    throw new AnalyzeBillError(parsed.data.error.code, parsed.data.error.message || GENERIC_FAILURE_MESSAGE);
  }
  // The server only ever emits FieldValidationError.field values from its own
  // BillData keys (see functions/lib/validation/pipeline.ts) — the looser
  // z.string() here just validates response shape, not the field-key union.
  return {
    requestId: parsed.data.requestId,
    billData: parsed.data.billData,
    warnings: parsed.data.warnings as FieldValidationError[],
  };
}
